<template>
	<div
		class="message-template-detail bg-white-custom"
    :class="{
      'border': scope == 'admin',
      'px-3': scope == 'admin',
      'py-3': scope == 'admin',
    }"
	>
		<b-overlay :show="isLoading" rounded="sm">
			<ConveyRequiredStatement class="mb-2" />
			<div v-if="errorMsg" class="text-danger mb-2">{{ errorMsg }}</div>
			<b-row>
				<b-col cols="12">
					<b-form-group
						id="fieldset-1"
						label="Template Label:"
						placeholder="Enter template label..."
						label-for="input-label"
            class="required-form-field"
						style="font-weight:500"
					>
						<b-form-input id="input-label" v-model="currentTemplate['name']" trim></b-form-input>
						<b-form-invalid-feedback :state="false">{{ errors['name'] }}</b-form-invalid-feedback>
					</b-form-group>
				</b-col>
				<b-col cols="12">
					<b-form-group
						id="fieldset-2"
						label="Template Text:"
						placeholder="Enter template text..."
						label-for="input-text"
						style="font-weight:500"
            class="required-form-field"
					>
						<b-form-textarea
							id="template-text"
							v-model="currentTemplate['text']"
							placeholder="Enter message text..."
							rows="3"
							max-rows="6"
						></b-form-textarea>
						<b-form-invalid-feedback :state="false">
							{{ errors['text'] }}
						</b-form-invalid-feedback>
					</b-form-group>
				</b-col>
			</b-row>
			<div class="d-flex align-items-center justify-content-end gap-2">
				<b-button
					variant="success"
					@click="saveTemplate()"
					pill
					size="sm"
				>
					<b-spinner v-if="savePending === true" small></b-spinner>
					<span v-else>Save</span>
				</b-button>
				<b-button
					v-if="currentTemplate && currentTemplate.template_id"
					pill
					@click="removeTemplate()"
					variant="danger"
					size="sm"
				>
					<b-spinner v-if="deletePending === true" small></b-spinner>
					<span v-else>Delete</span>
				</b-button>
			</div>
		</b-overlay>
	</div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";
import { EventBus } from "@/events/eventBus";
import ConveyRequiredStatement from "@/components/ConveyRequiredStatement";

export default {
	name: "TemplateDetail",
	mixins: [formatter, errorHandler],
	components: { ConveyRequiredStatement },
	props: {
		scope: {
			type: String,
			default: "user",
		},
    templateId: String
	},
	data() {
		return {
			isLoading: false,
			editTemplate: false,
			currentTemplate: {},
			errors: {},
			errorMsg: null,
			deletePending: false,
			savePending: false,
			fetchAbortController: null,
		};
	},
	computed: {
		...mapGetters("user", ["currentToken"]),
		...mapGetters("agency", ["currentAgencyId"]),
	},
	created() {
		if (this.templateId) {
			this.fetchTemplate();
		} else {
			this.editTemplate = true;
			this.currentTemplate = {};
		}
	},
	methods: {
		async fetchTemplate() {
			try {
				this.fetchAbortController = new AbortController();
				this.isLoading = true;
				const resp = await this.$ConveyApi.templatesApi.getAgencyMessageTemplate({
					agencyId: this.currentAgencyId,
					templateId: this.templateId,
					signal: this.fetchAbortController.signal,
				});
				this.currentTemplate = resp.data.data;
			} catch (err) {
				if (!this.isApiCanceled(err)) {
					this.errorMsg = "Error loading template.";
					console.log("[TemplateDetail:fetchTemplate] ERROR: ", err);
				}
			} finally {
				this.isLoading = false;
			}
		},
		async saveTemplate() {
			this.errorMsg = null;
			this.errors = {};
			if (!this.currentTemplate.name || this.currentTemplate.name == "")
				this.errors["name"] = "Template label is required";
			if (!this.currentTemplate.text || this.currentTemplate.text == "")
				this.errors["text"] = "Template message is required";
			if (Object.keys(this.errors).length > 0) 
				return false;             

			try {
				this.savePending = true;

				const payload = {
					name: this.currentTemplate.name,
					text: this.currentTemplate.text
				};
				
				if (this.scope === "admin") {
					if (this.templateId) {
						await this.$ConveyApi.templatesApi.updateAgencyMessageTemplate({
							agencyId: this.currentAgencyId,
							templateId: this.templateId,
							payload,
						});
					} else {
						await this.$ConveyApi.templatesApi.createAgencyMessageTemplate({
							agencyId: this.currentAgencyId,
							payload,
						});
					}
				} else {
					if (this.templateId) {
						await this.$ConveyApi.templatesApi.updateUserMessageTemplate({
							templateId: this.templateId,
							payload,
						});
					} else {
						await this.$ConveyApi.templatesApi.createUserMessageTemplate({
							payload,
						});
					}
				}
				
				this.$toastr("success", "Template has been saved.");
				this.currentTemplate = {};

				if (this.scope == "user") {
					EventBus.$emit("close-modal");
					EventBus.$emit("refresh-templates");
				} else {
					this.$router.push({ path: "/admin/templates" }).catch(() => {});
				}
			} catch (err) {
				this.errorMsg = err.response.data.data.error;
			} finally {
				this.savePending = false;
			}
		},
		async removeTemplate() {
			if (confirm("Are you sure you want to remove this message template?") != true)
				return;

			try {
				this.deletePending = true;
				if (this.scope === "admin") {
					await this.$ConveyApi.templatesApi.deleteAgencyMessageTemplate({
						agencyId: this.currentAgencyId,
						templateId: this.templateId,
					});
				} else {
					await this.$ConveyApi.templatesApi.deleteUserMessageTemplate({
						templateId: this.templateId
					});
				}

				this.$toastr("success", "Template has been removed.");
				this.currentTemplate = {};

				if (this.scope == "admin") {
					this.$router.push({ path: "/admin/templates" }).catch(() => {});
				}	else {
					EventBus.$emit("close-modal");
					EventBus.$emit("refresh-templates");
				}
			} catch (err) {
				if (err.response && err.response.data && err.response.data.data) {
					this.errorMsg = err.response.data.data.error;
				} else {
					this.errorMsg = "An error occurred";
				}
			} finally {
				this.deletePending = false;
			}
		},

	},
	beforeDestroy() {
		if (this.fetchAbortController) {
			this.fetchAbortController.abort();
			this.fetchAbortController = null;
		}
	}
};
</script>

<style lang="scss">
.message-template-detail {
  max-width: 800px;
  width: 100%;
  height: auto;
	border-radius: 1rem;
}
</style>
